import { Add, Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { VehicleDto, VehicleRecallDto } from 'dtos'
import { useEffect, useState } from 'react'
import RecallCampaignAddEditDialog from './RecallCampaignAddEditDialog'
import * as controllers from 'controllers'
// import { useLoadingState } from 'hooks'
import { ExplanationAccordion, GreenText, SortableTableCell } from 'components'
import { SortDirection } from 'localConstants/sortDirection'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { VehicleRecallParameters } from 'parameters'
import { useDebounce } from 'utils'
import { DEBOUNCE_DELAY } from 'localConstants'

interface RecallCampaignListCardProps {
  vehicle: VehicleDto
}

export default function RecallCampaignListCard({ vehicle }: RecallCampaignListCardProps) {
  const [isGettingRecallCampaigns, setIsGettingRecallCampigns] = useState<boolean>(false)
  const [recallCampaign, setRecallCampaign] = useState<VehicleRecallDto>(new VehicleRecallDto())
  const [vehicleRecallCampaigns, setVehicleRecallCampaigns] = useState<VehicleRecallDto[]>([])
  const [isRecallCampaignSaving, setIsRecallCampaignSaving] = useState<boolean>(false)
  const [recallCampaignAddEditDialogOpen, setRecallCampaignAddEditDialogOpen] = useState<boolean>(false)
  // const [loadingState, setLoadingState] = useLoadingState({
  //   isCreatingVehicleRecall: false,
  //   isUpdatingVehicleRecall: false
  // })

  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<{
    page: number
    pageSize: number
    sortBy: string
    sortDirection: SortDirection

    user?: string
    setDate?: string
    sentTo?: string
  }>({
    page: 0,
    pageSize: 5,
    sortBy: 'user',
    sortDirection: 'desc'
  })

  const onChangeSortByDirection = (sortBy: string) => {
    setParameters(prev => ({
      ...parameters,
      sortBy,
      sortDirection: prev.sortBy === sortBy && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }))
  }

  const formatDate = (date: any) => {
    return moment(date).format('MM/DD/YYYY hh:mma')
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleRecallCampaigns(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getVehicleRecallCampaigns = useDebounce((vehicleGuid: string, parameters: VehicleRecallParameters) => {
    setIsGettingRecallCampigns(true)
    controllers
      .getVehicleRecallCampaigns(vehicleGuid, parameters)
      .then(response => {
        setVehicleRecallCampaigns(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingRecallCampigns(false)
      })
  }, DEBOUNCE_DELAY)

  const handleSave = (values: VehicleRecallDto) => {
    const requestMethod = recallCampaign.vehicleRecallGuid
      ? controllers.updateVehicleRecallCampaign
      : controllers.createVehicleRecallCampaign

    setIsRecallCampaignSaving(true)
    requestMethod(values, vehicle.vehicleGuid!)
      .then(response => {
        setRecallCampaignAddEditDialogOpen(false)
        enqueueSnackbar('Recall Campaign Saved Successfully!', { variant: 'success' })
        setRecallCampaign(values)
        getVehicleRecallCampaigns(vehicle.vehicleGuid, parameters)
      })
      .finally(() => {
        setIsRecallCampaignSaving(false)
      })
  }

  const handleEdit = (recallCampaign: VehicleRecallDto) => {
    setRecallCampaign(recallCampaign)
    setRecallCampaignAddEditDialogOpen(true)
  }

  return (
    <>
      <RecallCampaignAddEditDialog
        initialValues={recallCampaign}
        isLoading={isRecallCampaignSaving}
        onClose={() => {
          setRecallCampaignAddEditDialogOpen(false)
          setRecallCampaign(new VehicleRecallDto())
        }}
        onSave={handleSave}
        open={recallCampaignAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={8}>
                <Typography fontWeight='bold' variant='h5'>
                  Recall Campaigns
                </Typography>
              </Grid>
              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setRecallCampaign(new VehicleRecallDto())
                    setRecallCampaignAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD&nbsp;
                  <Add fontSize='small' />
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {isGettingRecallCampaigns && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                  <Grid>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!isGettingRecallCampaigns && (
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='recallNoRecall'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Recall/No Recall
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='recallCampaignId'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Recall Campaign ID
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='recallStatus'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Recall Status
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='recallCountry'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Recall Country
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='repairCountry'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Repair Country
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='repairStatus'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Repair Status
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='createdDateTime'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Created Date/Time
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='repairedDateTime'
                            sortBy={parameters.sortBy}
                            sortDirection={parameters.sortDirection}
                          >
                            Repaired Date/Time
                          </SortableTableCell>

                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehicleRecallCampaigns.map(recallCampaign => (
                          <TableRow key={recallCampaign.recallCampaignId}>
                            <TableCell>{recallCampaign.vehicleRecallStatus?.name}</TableCell>
                            <TableCell>{recallCampaign.recallCampaignId}</TableCell>
                            <TableCell>
                              {(recallCampaign.vehicleRecallStatus?.name === 'No Recall' &&
                                recallCampaign.canadianOEMSiteScreenshotFileName !== null &&
                                recallCampaign.usaOEMSiteScreenshotFileName !== null) ||
                              (recallCampaign.vehicleRecallStatus?.name === 'Recall' &&
                                recallCampaign.vehicleRecallRepairStatus?.name === 'Repaired' &&
                                (recallCampaign.usaOEMSiteScreenshotFileName !== null ||
                                  recallCampaign.canadianOEMSiteScreenshotFileName !== null) &&
                                recallCampaign.repairOrderFileName !== null)
                                ? 'Closed'
                                : 'Open'}
                            </TableCell>

                            <TableCell>{recallCampaign.recallCountry}</TableCell>
                            <TableCell>{recallCampaign.repairCountry}</TableCell>
                            <TableCell>{recallCampaign.vehicleRecallRepairStatus?.name}</TableCell>
                            <TableCell>{formatDate(recallCampaign.createdDateTime)}</TableCell>
                            <TableCell>
                              {recallCampaign.repairedDateTime ? formatDate(recallCampaign.repairedDateTime) : '-'}
                            </TableCell>

                            <TableCell>
                              <Button
                                color='primary'
                                onClick={() => handleEdit(recallCampaign)}
                                variant='text'
                                endIcon={<Edit />}
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            SelectProps={{ fullWidth: false }}
                            count={totalCount}
                            rowsPerPage={parameters.pageSize}
                            page={parameters.page}
                            onPageChange={(_, page) => {
                              setParameters({
                                ...parameters,
                                page
                              })
                            }}
                            onRowsPerPageChange={e => {
                              setParameters({
                                ...parameters,
                                page: 0,
                                pageSize: e.target.value as unknown as number
                              })
                            }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`Recall status is automatically determined by VTS2 and is not user editable.

Recall Status is automatically set to "Closed" when:
   1. "No Recall" + CA OEM Site SS show no open recalls + USA show no open recalls OR
   2. "Recall" + Repair Status = "Repaired" + USA OEM Site SS uploaded OR CA OEM Site SS Uploaded + RO Uploaded”

Repaired Date Time is set when the recall is set to "Recall" and Repair Status is set to "Repaired". If the recall is set to "No Recall", Repaired Date/Time will be blank.`}
                </GreenText>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
